import { HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup } from "@angular/forms";
import { Budget, BudgetSummary } from "@interfaces/budget-response";
import { BudgetsQuery } from "@interfaces/budgets-query";
import { ModalService } from "@services/modal.service";
import { MvService } from "@services/mv.service";
import moment from "moment";
import { PaginatorState } from "primeng/paginator";
import { Subscription, debounceTime, forkJoin, skip } from "rxjs";

@Component({
  selector: "app-budget-selection",
  templateUrl: "./budget-selection.component.html",
  styleUrls: ["./budget-selection.component.scss"]
})
export class BudgetSelectionComponent implements OnInit {
  private static readonly NO_PERMISSION_KEYWORD = "não tem permissão";

  @Output() closed = new EventEmitter<void>();
  @Output() budget = new EventEmitter<Budget>();
  @Input() active = false;

  $: Subscription[] = [];

  query = signal<BudgetsQuery>({
    paginacao: 1,
    pageSize: 5,
    dadosCliente: true,
    dataFim: null,
    dataInicio: null,
    pesquisa: null,
    statusObra: 0
  });

  search = new FormGroup({
    cliente: new FormControl("")
  });

  totalBudgets = signal(0);

  budgets = signal<BudgetSummary[]>([]);

  loadErrorMessage = signal<string | null>(null);

  queryChange$ = toObservable(this.query)
    .pipe(skip(1))
    .subscribe((query) => {
      this.fetchBudgets(query);
    });

  get numeroPedido() {
    return (budget: BudgetSummary) =>
      `${moment(budget.dataCriacaoObra).format("YYMMDD")}${budget.idObra}`;
  }

  constructor(
    private readonly mvService: MvService,
    private readonly modalService: ModalService,
    private readonly destroyRef: DestroyRef
  ) {
    this.destroyRef.onDestroy(() => {
      this.$.forEach((sub) => sub.unsubscribe());
    });
  }

  ngOnInit(): void {
    this.fetchBudgets(this.query());
    this.onSearch();
  }

  fetchBudgets(query: BudgetsQuery) {
    this.$.push(
      this.mvService.fetchBudgets(query).subscribe({
        next: ({ data, meta }) => {
          this.loadErrorMessage.set(null);
          this.budgets.set(data);
          this.totalBudgets.set(meta.total);
        },
        error: (error: HttpErrorResponse) => {
          const apiMessage = this.extractErrorMessage(error);

          if (this.isNoPermissionError(apiMessage)) {
            const permissionMessage =
              "Você não tem permissão para visualizar a lista de orçamentos. Solicite ao administrador a liberação de acesso.";

            this.loadErrorMessage.set(permissionMessage);
            this.modalService.error(permissionMessage);
            return;
          }

          const fallbackMessage =
            apiMessage ?? "Não foi possível carregar a lista de orçamentos.";

          this.loadErrorMessage.set(fallbackMessage);
          this.modalService.error(fallbackMessage);
        }
      })
    );
  }

  private extractErrorMessage(error: HttpErrorResponse): string | undefined {
    const candidates = [
      error?.error?.message,
      error?.error?.message?.message,
      error?.error?.error?.message,
      error?.message
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.length > 0) {
        return candidate.join("\n");
      }

      if (typeof candidate === "string" && candidate.trim()) {
        return candidate;
      }
    }

    return undefined;
  }

  private isNoPermissionError(message?: string): boolean {
    if (!message) {
      return false;
    }

    return message
      .toLocaleLowerCase("pt-BR")
      .includes(BudgetSelectionComponent.NO_PERMISSION_KEYWORD);
  }

  onPageChange(state: PaginatorState) {
    this.query.update((query) => ({
      ...query,
      paginacao: (state.page ?? 1) + 1
    }));
  }

  selectBudget(budget: BudgetSummary) {
    this.$.push(
      forkJoin([
        this.mvService.fetchBudget(budget.idObra),
        this.mvService.fetchObra(budget.idObra)
      ]).subscribe({
        next: ([selectedBudget, obra]) => {
          this.budget.emit({
            ...selectedBudget,
            obra
          });
        }
      })
    );
  }

  onSearch() {
    this.$.push(
      this.search.controls.cliente.valueChanges
        .pipe(debounceTime(500), skip(1))
        .subscribe((value) => {
          this.query.update((query) => ({
            ...query,
            pesquisa: value
          }));
        })
    );
  }
}
